<template>
  <section class="section">
    <b-loading isFullPage :active="isLoading" />

    <div
      v-if="currentDocument && currentDocument.process"
      class="wrapper-header"
    >
      <div class="is-flex is-align-items-center">
        <div class="custom-pointer" @click="goBack()">
          <b-icon
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            icon="arrow-left-thick"
            size="is-medium"
          />
        </div>
        <div v-if="currentDocument.process.length" class="header-item-right">
          <b-tooltip
            :label="currentDocument.processFile.attachment_name"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            :active="showDocNameTooltip"
            class="tooltip-break-word"
            size="is-large"
            multilined
          >
            <h2 class="doc-name is-size-4 has-text-weight-semibold">
              {{ currentDocument.processFile.attachment_name || "" }}
            </h2>
          </b-tooltip>
          <div class="is-size-6 spacing-pr-30">
            <label class="has-text-weight-semibold">{{ General.NUP }}: </label>
            <template
              v-if="
                currentDocument.process.length &&
                  isValidNUP(currentDocument.process[0])
              "
            >
              <routerLink
                v-for="(process, index) in currentDocument.process"
                :key="index"
                class="is-clickable link"
                role="link"
                rel="search tag"
                :to="`/app/processos/detalhamento/${process.id}`"
              >
                {{
                  `${applyMaskNUP(process.nup)}
                  ${currentDocument.process.length - 1 > index ? " - " : ""}`
                }}
              </routerLink>
            </template>
            <template v-else>
              <span>{{ General.TEXTS.NUP_NOT_GENERATED }}</span>
            </template>
            <b-tooltip
              v-if="isConfidential"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              position="is-top"
              :label="secrecyName"
              class="ml-5"
              role="tooltip"
            >
              <div class="circle-red">
                <b-icon type="is-white" icon="lock" customSize="mdi-inherit" />
              </div>
            </b-tooltip>
          </div>
          <div
            v-if="currentDocument.processFile.document_type"
            class="is-size-6"
          >
            <label class="has-text-weight-semibold">{{ General.TYPE }}:</label>
            {{ currentDocument.processFile.document_type.type }}
          </div>
          <div class="is-size-6">
            <span class="has-text-weight-semibold"
              >{{ General.ADDED_ON }}:</span
            >
            {{
              new Date(currentDocument.processFile.date)
                | dateFormat("DD/MM/YYYY HH:mm:ss")
            }}
            <span class="has-text-weight-semibold">{{ General.PER }}</span>
            {{ getOriginCapacity }}
          </div>
        </div>
      </div>
      <b-tooltip
        v-if="
          currentDocument.process.length &&
            isValidNUP(currentDocument.process[0])
        "
        multilined
        :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
        position="is-left"
        size="is-large"
        role="tooltip"
        :always="showPDFTooltip"
      >
        <template v-slot:content>
          <section v-if="Boolean(systemAbbreviation)" class="has-text-left">
            {{ getViewTooltipLabelExternalSystem }}
          </section>
          <section
            v-else
            class="has-text-left"
            @mouseout="setShowPDFTooltip(false)"
            @mousemove="setShowPDFTooltip(true)"
          >
            <p class="has-text-color-1 has-text-weight-bold">
              {{ App.ProcessViewer.VIEW_DOCUMENTS }}
            </p>
            {{ App.ProcessViewer.MESSAGE_NEW_VIEWER }}
          </section>
        </template>
        <b-button
          :label="General.TO_VIEW"
          :type="isContrast ? 'is-white' : 'is-primary'"
          iconLeft="folder-search"
          :expanded="isMobile"
          :disabled="Boolean(systemAbbreviation)"
          :aria-description="General.ARIA_LABEL.VIEW_DOCUMENT"
          @click="getCompleteProcessFileStream(currentDocument.process[0].id)"
          @mouseover="setShowPDFTooltip(true)"
          @mousemove="setShowPDFTooltip(false)"
        />
      </b-tooltip>
    </div>
    <div v-else class="wrapper-header">
      <div>
        <b-icon
          :type="isContrast ? 'is-white' : 'is-blue-oil'"
          icon="arrow-left-thick"
          size="is-medium"
        />
      </div>
      <div class="header-item-right">
        <b-skeleton :active="isLoading" width="30%" height="1.6rem" animated />
        <b-skeleton :active="isLoading" width="20%" animated />
        <b-skeleton :active="isLoading" width="20%" animated />
        <b-skeleton :active="isLoading" width="90%" animated />
      </div>
      <b-button
        :label="General.VIEW_PROCESS"
        :type="isContrast ? 'is-white' : 'is-primary'"
        iconLeft="folder-search"
      />
    </div>

    <hr class="custom-hr" />
    <section v-if="currentDocument" class="content spacing-p-20">
      <div class="columns is-marginless">
        <div class="column is-9 is-paddingless is-flex">
          <PDFViewerMobile
            v-if="currentDocument.urlFile && isMobile"
            :source="currentDocument.urlFile"
            class="pdf-viewer-mobile"
            @download="downloadOriginalFile()"
          />
          <PDFViewer
            v-if="currentDocument.urlFile && !isMobile"
            :fileURL="currentDocument.urlFile"
            :viewerRules="pdfViewerRules"
          />
        </div>
        <div class="column is-3 custom-column-right">
          <ul class="custom-nav">
            <template v-if="isDOE.isNotPublished">
              <li v-if="currentDocument.is_signed">
                <b-button
                  :label="General.YOU_HAVE_ALREADY_SIGNED_THIS_DOC"
                  disabled
                  expanded
                  type="is-light"
                  class="has-text-black"
                  size="is-medium"
                />
              </li>
              <li
                v-else-if="
                  (validateUserResponsible ||
                    validateSignPermission ||
                    validateProcessFilePermission) &&
                    processHasCanTramit
                "
              >
                <b-button
                  :label="General.SIGN"
                  :aria-description="General.ARIA_LABEL.MODAL_SIGN_DOCUMENT"
                  :disabled="isNoEffect || isLargeFile"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  expanded
                  size="is-medium"
                  @click="!isNoEffect && showModalSignDocument()"
                />
              </li>
              <li
                v-if="
                  !currentDocument.is_signed &&
                    validateSignPermission &&
                    processHasCanTramit
                "
              >
                <b-button
                  :label="General.DECLINE"
                  :aria-description="General.ARIA_LABEL.MODAL_REFUSE_SIGN"
                  type="is-orange"
                  :disabled="isNoEffect"
                  expanded
                  size="is-medium"
                  @click="!isNoEffect && declineRequest()"
                />
              </li>
              <li v-if="processHasCanTramit && validateRequestSignature()">
                <b-button
                  :label="General.REQUEST_SUBSCRIPTION"
                  :aria-description="General.ARIA_LABEL.MODAL_REQUEST_SIGNATURE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :disabled="isNoEffect || isLargeFile"
                  expanded
                  size="is-medium"
                  @click="!isNoEffect && showModalDocSignature()"
                />
              </li>
              <li
                v-if="
                  currentDocument.can_give_knowledge &&
                    currentDocument.process[0].can_tramit
                "
              >
                <b-button
                  :label="General.GIVE_KNOWLEDGE"
                  :aria-description="General.ARIA_LABEL.MODAL_GIVE_KNOWLEDGE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :disabled="isNoEffect"
                  expanded
                  size="is-medium"
                  @click="!isNoEffect && showModalGiveKnowledge()"
                />
              </li>
              <li v-if="canMakeNoEffect && processHasCanTramit">
                <b-button
                  :label="
                    !isNoEffect
                      ? General.NO_EFFECT
                      : General.THIS_DOC_IS_ALREADY_NO_EFFECT
                  "
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :disabled="isNoEffect"
                  expanded
                  size="is-medium"
                  @click="!isNoEffect && showModalNoEffect()"
                />
              </li>
              <li v-if="canMakeNoEffect && processHasCanTramit && isNoEffect">
                <b-tooltip
                  :label="App.ModalRevertNoEffect.BUTTON_TOOLTIP"
                  multilined
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  :position="isMobile ? 'is-top' : 'is-left'"
                  role="tooltip"
                  class="is-flex tooltip-break-word"
                >
                  <b-button
                    :label="App.ModalRevertNoEffect.BUTTON_LABEL"
                    :type="isContrast ? 'is-white' : 'is-primary'"
                    expanded
                    size="is-medium"
                    @click="showModalRestoreEffect()"
                  />
                </b-tooltip>
              </li>
              <li v-if="hasPermissionValidate && processHasCanTramit">
                <b-tooltip
                  :label="General.MESSAGES.ALERT_AUTHENTICATED_FILE"
                  :type="isContrast ? 'is-white' : 'is-blue-oil'"
                  :active="!currentDocument.can_authenticate"
                  :position="isMobile ? 'is-top' : 'is-left'"
                  class="is-flex tooltip-break-word"
                  size="is-large"
                  multilined
                >
                  <b-button
                    :label="General.AUTHENTICATE"
                    :aria-description="General.ARIA_LABEL.AUTHENTICATE_DOCUMENT"
                    :type="isContrast ? 'is-white' : 'is-primary'"
                    :disabled="isValidated"
                    expanded
                    size="is-medium"
                    @click="confirmValidate()"
                  />
                </b-tooltip>
              </li>
              <li
                v-if="
                  currentDocument.show_draft_files && hasPendingSignatureToUser
                "
              >
                <b-button
                  :label="General.ATTACHED_DOCUMENT_EXISTS"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.SHARED_DOCUMENTS"
                  expanded
                  size="is-medium"
                  @click="openShareDetail(currentDocument)"
                />
              </li>
              <li v-if="currentDocument.urlFile">
                <b-button
                  :label="General.DOWNLOAD_PRINTABLE_VERSION"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.DOWNLOAD_PRINT_VERSION"
                  expanded
                  size="is-medium"
                  @click="checkIsConfidential(downloadOriginalFile)"
                />
              </li>
              <li v-if="documentSigned">
                <b-button
                  :label="General.VIEW_DIGITAL_FILE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.VIEW_DIGITAL"
                  :disabled="isNoEffect"
                  expanded
                  size="is-medium"
                  @click="
                    !isNoEffect && checkIsConfidential(viewSignedDocument)
                  "
                />
              </li>
            </template>
            <template>
              <li
                v-if="
                  hasPermissionManageDOE &&
                    isDOE.isProgrammed &&
                    processHasCanTramit
                "
                :class="{ 'custom-opacity': !hasDoeProcessFileId }"
              >
                <b-button
                  :label="General.REGISTER_DOE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.REGISTER_DOE"
                  expanded
                  size="is-medium"
                  @click="openModalPublish()"
                />
              </li>
              <li
                v-if="
                  hasPermissionManageDOE &&
                    isDOE.isProgrammed &&
                    processHasCanTramit
                "
                :class="{ 'custom-opacity': !hasDoeProcessFileId }"
              >
                <b-button
                  :label="General.SEND_TO_DOE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.SEND_DOE"
                  expanded
                  size="is-medium"
                  @click="openModalSendToPrintShop"
                />
              </li>
              <li
                v-if="
                  validateUserResponsible &&
                    hasPermissionAuthorizeDOE &&
                    isDOE.authorize &&
                    processHasCanTramit
                "
              >
                <b-button
                  :label="General.AUTHORIZE_DOE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.AUTHORIZE_DOE"
                  expanded
                  size="is-medium"
                  @click="showModalAuthorizeDOE"
                />
              </li>
              <li
                v-if="
                  hasPermissionManageDOE &&
                    isDOE.seeButtons &&
                    processHasCanTramit
                "
                :class="{ 'custom-opacity': !hasDoeProcessFileId }"
              >
                <b-button
                  :label="
                    isProgrammed
                      ? General.CHANGE_SCHEDULE
                      : General.SCHEDULE_PUBLICATION_DOE
                  "
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="
                    isProgrammed
                      ? General.ARIA_LABEL.CHANGE_SCHEDULE_DOE
                      : General.ARIA_LABEL.SCHEDULE_DOE
                  "
                  expanded
                  size="is-medium"
                  @click="openModalSchedulePublication"
                />
              </li>
              <li
                v-if="checkDeclinePublicationPermission && processHasCanTramit"
              >
                <b-button
                  :label="General.DECLINE_PUBLICATION"
                  type="is-orange"
                  :aria-description="General.ARIA_LABEL.REFUSE_DOE"
                  expanded
                  size="is-medium"
                  @click="openModalDeclinePublication"
                />
              </li>
              <li v-if="currentDocument.urlFile && isDOE.seeButtons">
                <b-button
                  :label="General.DOWNLOAD_PRINTABLE_VERSION"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.DOWNLOAD_PRINT_VERSION"
                  expanded
                  size="is-medium"
                  @click="checkIsConfidential(downloadOriginalFile)"
                />
              </li>
              <li v-if="documentSigned && isDOE.seeButtons">
                <b-button
                  :label="General.VIEW_DIGITAL_FILE"
                  :type="isContrast ? 'is-white' : 'is-primary'"
                  :aria-description="General.ARIA_LABEL.VIEW_DIGITAL"
                  :disabled="isNoEffect"
                  expanded
                  size="is-medium"
                  @click="
                    !isNoEffect && checkIsConfidential(viewSignedDocument)
                  "
                />
              </li>
            </template>
          </ul>

          <section class="wrapper-file-history">
            <template v-if="documentHistory.length">
              <h2 class="title is-6 has-text-color-8">
                {{ General.HISTORY_DOCUMENT }}
              </h2>
              <div class="wrapper-history-list has-text-left spacing-my-10">
                <div
                  v-for="(item, index) in documentHistory"
                  :key="index"
                  :class="item.status.toLowerCase()"
                >
                  <div class="spacing-mr-5"><div></div></div>
                  <div class="is-size-7">
                    <template v-if="item.status === STATUS.PENDING">
                      <strong>
                        {{ General.TEXTS.AWAITING_SIGNATURE_FROM }}
                      </strong>
                      {{ item.user_name }}
                      {{ General.TEXTS.SINCE }}
                      {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.SIGNED">
                      {{ item.user_name }}
                      ({{
                        item.is_external
                          ? General.EXTERNAL_USER
                          : getCapacityFormat(item.capacity)
                      }})
                      <strong>
                        {{
                          item.is_external
                            ? General.TEXTS.SIGNED_EXTERNAL
                            : General.TEXTS.SIGNED
                        }}</strong
                      >
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.REFUSED">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="modalRefuseJustification(item)"
                      >
                        <strong>
                          {{ General.REFUSED }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.REFUSED_SUBSCRIPTION_2 }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.VALIDATED">
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.DOCUMENT_AUTHENTICATED }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.GIVE_KNOWLEDGE">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="modalViewGiveKnowledge(item)"
                      >
                        <strong>{{ General.TEXTS.GAVE_KNOWLEDGE_TO }}</strong>
                      </a>
                      <span>
                        do documento para
                        {{ item.user_to_give_knowledge_name }} em
                        {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                        {{ new Date(item.date) | dateFormat("HH:mm") }}
                      </span>
                    </template>

                    <template
                      v-else-if="item.status === STATUS.REVERT_NO_EFFECT"
                    >
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.REVERT_TURN_NO_EFFECT }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.NO_EFFECT">
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.TURNED_NO_EFFECT }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.TO_PUBLISH_DOE">
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.MARKED_TO_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template
                      v-else-if="item.status === STATUS.REMOVE_TO_PUBLISH"
                    >
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.UNMARKED_TO_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.AUTHORIZED_DOE">
                      {{ item.user_name }}
                      <strong>
                        {{ General.TEXTS.AUTHORIZED_PUBLICATION }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.PUBLISHED_DOE">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalPublish(item)"
                      >
                        <strong>
                          {{ General.PUBLISHED.toLowerCase() }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.THE_PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template
                      v-else-if="item.status === STATUS.CHANGE_PUBLISH_DATE_DOE"
                    >
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalChangePublish(item)"
                      >
                        <strong>
                          {{ General.CHANGE_PUBLISH_DATE.toLowerCase() }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.OF_PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.PROGRAMMED_DOE">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalDetailProgrammed(item)"
                      >
                        <strong>
                          {{ General.PROGRAMMED.toLowerCase() }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template
                      v-else-if="item.status === STATUS.CHANGE_PROGRAM_DATE_DOE"
                    >
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalDetailProgrammed(item, true)"
                      >
                        <strong>
                          {{ General.CHANGE_PROGRAM_DATE.toLowerCase() }} de
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.REFUSED_DOE">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalDetailDeclinePublication(item)"
                      >
                        <strong>
                          {{ General.REFUSED }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.THE_PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else-if="item.status === STATUS.MANDATORY">
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalMandatorySignature(item)"
                      >
                        <strong>
                          {{ General.CHANGED_SCHEDULE.toLowerCase() }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.OF_SIGNATURE_TO_DOCUMENT }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template
                      v-else-if="item.status === STATUS.PROGRAM_CANCELED_DOE"
                    >
                      {{ item.user_name }}
                      <a
                        class="is-underlined has-text-color-7"
                        role="button"
                        @click="openModalCanceled(item)"
                      >
                        <strong>
                          {{ General.CANCELED.toLowerCase() }}
                        </strong>
                      </a>
                      <strong>
                        {{ General.TEXTS.THE_PUBLICATION_ON_DOE }}
                      </strong>
                      em {{ new Date(item.date) | dateFormat("DD/MM/YYYY") }} às
                      {{ new Date(item.date) | dateFormat("HH:mm") }}
                    </template>

                    <template v-else>
                      {{ item }}
                    </template>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="isLoading">
              <h2 class="title is-6 has-text-blue-dark">
                {{ General.HISTORY_DOCUMENT }}
              </h2>
              <b-skeleton :active="isLoading" animated />
              <b-skeleton :active="isLoading" animated />
              <b-skeleton :active="isLoading" animated />
            </template>
            <template v-else>
              <h2 class="title is-6 has-text-color-7">
                {{ General.DOCUMENT_WITHOUT_HISTORY }}
              </h2>
              <b-icon
                icon="file-document"
                size="is-medium"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
              />
            </template>
          </section>
        </div>
      </div>
      <div class="spacing-py-15"></div>
    </section>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import PDFViewer from "@/components/PDFViewer";
import PDFViewerMobile from "pdf-viewer-vue/dist/vue2-pdf-viewer";

import ModalDocSignature from "@/views/App/ModalDocSignature";
import ModalSignDocument from "@/views/App/ModalSignDocument";
import ModalSelectUser from "@/views/App/ModalSelectUser";
import ModalNoEffect from "@/views/App/ModalNoEffect";
import ModalRevertNoEffect from "@/views/App/ModalRevertNoEffect";

import { App, General } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import {
  DOC_SIGNATURES_STATUS as STATUS,
  MAX_SIGN_SIZE,
  USER_PERMISSIONS as userPermissions
} from "@/utils/enumerate";
import modalDeclineRequest from "@/views/App/ModalDeclineRequest";
import ModalViewGiveKnowledge from "@/views/App/ModalViewGiveKnowledge";
import ModalDocMandatorySignature from "@/views/App/ModalDocMandatorySignature";

import ModalWarning from "@/views/App/ModalWarning.vue";
import ModalShareDetail from "@/views/App/ModalShareDetail";
import ModalSchedulePublication from "@/views/App/ModalSchedulePublication.vue";
import ModalDeclinePublication from "@/views/App/ModalDeclinePublication.vue";
import ModalPublish from "@/views/App/ModalPublishDOE.vue";
import ModalChangePublishDOE from "@/views/App/ModalChangePublishDOE.vue";
import { downloadFileBlob } from "@/utils/fileUtility";
import { showErrorMessage } from "@/utils/messageUtility";

export default {
  name: "DocumentDetailsView",

  components: {
    PDFViewer,
    PDFViewerMobile
  },

  props: {
    id: {
      type: String,
      required: true
    },
    processId: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      App,
      General,
      STATUS,
      isLoading: true,
      showPDFTooltip: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      historyRouter: "getBreadcrumbs",
      currentDocument: "getCurrentFile",
      getCapacityById: "getCapacityById",
      hasPermission: "hasPermission",
      socketGetDocumentToUpdate: "socketGetDocumentToUpdate",
      isContrast: "isContrast",
      isConfidentialCheck: "isConfidentialCheck",
      isMobile: "isMobile"
    }),

    getViewTooltipLabelExternalSystem() {
      return `${General.MESSAGES.WARNING_INTEGRATION_PROCESS_SYSTEM}: ${this.systemAbbreviation}`;
    },

    isDOE() {
      const state = this.currentDocument.processFile?.current_doe_status;
      return {
        seeButtons: ["PROGRAMMED", "AUTHORIZED"].includes(state),
        isProgrammed: state === "PROGRAMMED",
        isPublished: state === "PUBLISHED",
        authorize: ["TO_PUBLISH", "REFUSED"].includes(state),
        isNotPublished: ["NOT_TO_PUBLISH", "REFUSED", "TO_PUBLISH"].includes(
          state
        )
      };
    },

    validateUserResponsible() {
      if (!this.currentDocument.process) return false;
      return this.currentDocument.process.some(
        process => process.user_responsible_id === this.user.id
      );
    },

    validateSignPermission() {
      if (!this.currentDocument.SignatureFileHistory) return false;
      return this.currentDocument.SignatureFileHistory.some(
        signature => signature.user_id === this.user.id
      );
    },

    validateProcessFilePermission() {
      if (!this.currentDocument.processFile) return false;
      return this.currentDocument.processFile.user_id === this.user.id;
    },

    hasPermissionNoEffect() {
      if (!this.currentDocument.processFile) return false;
      return (
        this.currentDocument.processFile.sequence &&
        this.hasPermission(userPermissions["NO_EFFECT"]) &&
        this.validateUserResponsible &&
        this.currentDocument.processFile.document_type?.make_no_effect &&
        !this.currentDocument.processFile.editable
      );
    },

    hasPermissionManageDOE() {
      return this.hasPermission(userPermissions["MANAGE_DOE"]);
    },

    hasPermissionAuthorizeDOE() {
      return this.hasPermission(userPermissions["AUTHORIZE_DOE"]);
    },

    hasPermissionValidate() {
      if (!this.currentDocument.processFile) return false;
      return (
        !this.currentDocument.processFile.no_effect &&
        this.hasPermission(userPermissions["VALIDATE_ARCHIVE"])
      );
    },

    isNoEffect() {
      if (!this.currentDocument.processFile) return false;
      return this.currentDocument.processFile.no_effect;
    },

    documentHistory() {
      if (!this.currentDocument.SignatureFileHistory) return [];
      let signatures = [...this.currentDocument.SignatureFileHistory];
      let shouldSort = false;

      if (this.currentDocument.DoeHistory) {
        this.currentDocument.DoeHistory.forEach(history =>
          signatures.push({
            ...history,
            status: `${history.status}_DOE`
          })
        );
        shouldSort = true;
      }

      if (this.currentDocument.processFile.is_validated) {
        this.currentDocument.processFile.validate_process_file.forEach(
          validate => {
            signatures.push({
              status: STATUS.VALIDATED,
              user_name: validate.user_name,
              date: validate.date
            });
          }
        );
        shouldSort = true;
      }

      if (this.currentDocument.DoeObrigatorySignatureHistory?.length) {
        const groupForHours = this.currentDocument.DoeObrigatorySignatureHistory.reduce(
          (acc, mandatory) => {
            const key = `${mandatory.date.slice(0, 13)}_${
              mandatory.user_request_id
            }`;

            if (acc[key]) acc[key].list.push(mandatory);
            else
              acc[key] = {
                ...mandatory,
                status: STATUS.MANDATORY,
                id: "",
                user_name: mandatory.user_request_name,
                user_id: mandatory.user_request_id,
                list: [mandatory]
              };

            return acc;
          },
          {}
        );
        signatures = signatures.concat(Object.values(groupForHours));
        shouldSort = true;
      }

      if (
        this.currentDocument.processFile.no_effect ||
        this.currentDocument.processFile.no_effect_date
      ) {
        signatures.push({
          status: STATUS.NO_EFFECT,
          user_name: this.currentDocument.processFile.no_effect_user_name,
          date: this.currentDocument.processFile.no_effect_date
        });
        shouldSort = true;
      }

      if (
        !this.currentDocument.processFile.no_effect &&
        this.currentDocument.processFile.revert_no_effect_user_name
      ) {
        signatures.push({
          status: STATUS.REVERT_NO_EFFECT,
          user_name: this.currentDocument.processFile
            .revert_no_effect_user_name,
          date: this.currentDocument.processFile.revert_no_effect_date
        });
        shouldSort = true;
      }

      if (this.currentDocument.GiveKnowledgeHistory.length) {
        this.currentDocument.GiveKnowledgeHistory.forEach(eachGiveKnowledge => {
          signatures.push({
            status: STATUS.GIVE_KNOWLEDGE,
            user_name: eachGiveKnowledge.user_name,
            user_email: eachGiveKnowledge.user_email,
            user_capacity: eachGiveKnowledge.user_capacity,
            user_to_give_knowledge_name:
              eachGiveKnowledge.user_to_give_knowledge_name,
            user_to_give_knowledge_email:
              eachGiveKnowledge.user_to_give_knowledge_email,
            user_to_give_knowledge_id:
              eachGiveKnowledge.user_to_give_knowledge_id,
            user_to_give_knowledge_capacity_id:
              eachGiveKnowledge.user_to_give_knowledge_capacity_id,
            observation: eachGiveKnowledge.observation,
            date: eachGiveKnowledge.date
          });
        });
        shouldSort = true;
      }

      if (shouldSort) {
        signatures.sort((current, next) => {
          return new Date(current.date) - new Date(next.date);
        });
      }

      return signatures;
    },

    isProgrammed() {
      return (
        this.currentDocument.processFile?.current_doe_status === "PROGRAMMED"
      );
    },
    canMakeNoEffect() {
      return (
        this.hasPermissionNoEffect &&
        this.currentDocument.processFile.can_make_no_effect
      );
    },
    isValidated() {
      const hasUserValidated = this.currentDocument.processFile.validate_process_file.some(
        validate => validate.user_id === this.user.id
      );
      return (
        !this.currentDocument.can_authenticate ||
        (this.currentDocument.processFile.is_validated && hasUserValidated)
      );
    },
    checkDeclinePublicationPermission() {
      return (
        (this.hasPermissionAuthorizeDOE &&
          this.currentDocument.processFile?.current_doe_status ===
            "TO_PUBLISH") ||
        (this.hasPermissionManageDOE && this.isDOE.seeButtons)
      );
    },

    documentSigned() {
      return this.currentDocument.SignatureFileHistory?.some(
        signature => signature.status === "SIGNED" && !signature.only_historical
      );
    },

    hasPendingSignatureToUser() {
      return this.currentDocument.SignatureFileHistory?.some(
        signature =>
          signature.status === "PENDING" &&
          signature.user_id === this.user.id &&
          !signature.only_historical
      );
    },

    getOriginCapacity() {
      const capacity = this.getCapacityById(
        this.currentDocument.processFile.capacity_id
      );
      if (!capacity) return "";
      return `${capacity.entity?.abbreviation}/${capacity.abbreviation} - ${capacity.description}`;
    },

    processOfCurrentDocument() {
      return this.currentDocument.process.find(
        process => process.id === this.processId
      );
    },

    processHasCanTramit() {
      return this.processId ? this.processOfCurrentDocument?.can_tramit : true;
    },

    showDocNameTooltip() {
      return (
        this.currentDocument.processFile.attachment_name.length >
        (this.isMobile ? 10 : 45)
      );
    },

    hasDoeProcessFileId() {
      return Boolean(this.$route.query.doeProcessFileId);
    },

    isLargeFile() {
      return this.currentDocument.processFile.filesize > MAX_SIGN_SIZE;
    },

    secrecyName() {
      return this.currentDocument.process[0].secrecy.name || "";
    },

    systemAbbreviation() {
      return this.currentDocument.process[0].external_system_abbreviation;
    },

    isConfidential() {
      const secrecyId = this.currentDocument.process[0].secrecy?.id;
      return this.isConfidentialCheck(secrecyId);
    },

    pdfViewerRules() {
      return this.isConfidential ? { toolbar: 0 } : {};
    },

    hasPermissionViewConfidential() {
      return this.hasPermission(userPermissions["VIEW_CONFIDENTIAL_PROCESS"]);
    }
  },

  watch: {
    id() {
      this.loadDocument();
    },

    socketGetDocumentToUpdate: {
      async handler(value) {
        if (value.files_id?.length && value.files_id[0] == this.id) {
          await this.getCurrentDocumentDetails({
            process_file_id: this.id,
            process_id: this.processId
          });
          this.clearDocumentToUpdate();
        }
      },
      deep: true
    }
  },

  beforeMount() {
    if (this.$route.query.signatureId)
      this.readNotification(this.$route.query.signatureId);
    this.loadDocument();
  },

  destroyed() {
    this.$store.commit("setCurrentFile", {});
  },

  methods: {
    ...mapActions({
      getCurrentDocumentDetails: "printableVersionRequest",
      authenticateFileRequest: "authenticateFileRequest",
      getCompleteProcessFileStream: "getCompleteProcessFileStream",
      requestListFileDraft: "requestListFileDraft",
      processFileRequest: "processFileRequest",
      readNotification: "readNotificationRequest",
      authorizeToPublish: "authorizeToPublishRequest",
      setToSentToPrintshop: "setToSentToPrintshop",
      requestFile: "requestDataProcessFile"
    }),

    ...mapMutations({
      setCurrentFileValidated: "setCurrentFileValidated",
      setCurrentProcessFileList: "setCurrentProcessFileList",
      clearDocumentToUpdate: "clearDocumentToUpdate"
    }),

    goBack() {
      this.$router.go(-1);
    },

    async reloadFile() {
      this.isLoading = true;
      await this.getCurrentDocumentDetails({
        process_file_id: this.id,
        process_id: this.processId
      });
      this.isLoading = false;
    },

    showModalDocSignature() {
      if (this.isLargeFile)
        return showErrorMessage(
          this.General.MESSAGES.ALERT_REQUEST_SIGNATURE_OVERSIZED_FILE
        );
      this.setCurrentProcessFileList([this.currentDocument.processFile]);
      this.$buefy.modal.open({
        parent: this,
        component: ModalDocSignature,
        props: {
          processId: this.processId,
          updateFiles: async () =>
            await this.getCurrentDocumentDetails({
              process_file_id: this.id,
              process_id: this.processId
            })
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showModalNoEffect() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalNoEffect,
        props: {
          processFileId: this.currentDocument.processFile.id,
          onClose: async () =>
            await this.getCurrentDocumentDetails({
              process_file_id: this.id,
              process_id: this.processId
            })
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showModalRestoreEffect() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalRevertNoEffect,
        props: {
          processFileId: this.currentDocument.processFile.id,
          processId: this.processId,
          listProcess: [this.currentDocument.process[0]],
          onClose: async () =>
            await this.getCurrentDocumentDetails({
              process_file_id: this.id,
              process_id: this.processId
            })
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openAlertMessage(message, type = "is-danger", duration = 8000) {
      this.snackToast.open({
        message: message,
        type: type,
        duration: duration
      });
    },

    handleDocumentSelectedSign() {
      const capacity_id = this.capacityToSign(
        this.currentDocument.SignatureFileHistory
      );
      this.$store.commit("setListSelectedDocuments", {
        documents: [
          {
            process_file: {
              ...this.currentDocument.processFile,
              process_id: this.processId,
              process: {
                nup:
                  this.currentDocument.process[0].nup ||
                  General.TEXTS.NUP_NOT_GENERATED
              }
            },
            signature_history: this.currentDocument.SignatureFileHistory,
            process_file_id: this.currentDocument.processFile.id
          }
        ],
        capacity_id
      });
    },

    showModalGiveKnowledge() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalSelectUser,
        props: {
          fileIds: [this.currentDocument.processFile],
          process: { id: this.processId },
          updateFiles: async () =>
            await this.getCurrentDocumentDetails({
              process_file_id: this.id,
              process_id: this.processId
            })
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    confirmValidate() {
      if (!this.isValidated)
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: this.General.CONFIRM,
            message: `${this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_VALIDATE_FILE}`,
            confirm: async () => {
              this.isLoading = true;

              await this.authenticateFileRequest([
                {
                  id: this.currentDocument.processFile.id,
                  is_validated: true
                }
              ]);
              await this.setCurrentFileValidated(true);
              await this.getCurrentDocumentDetails({
                process_file_id: this.id,
                process_id: this.processId
              });
              this.isLoading = false;
            }
          },
          hasModalCard: true,
          trapFocus: true
        });
    },

    showModalSignDocument() {
      if (this.isLargeFile)
        return showErrorMessage(
          this.General.MESSAGES.ALERT_SIGN_OVERSIZED_FILE
        );
      this.handleDocumentSelectedSign();

      this.$buefy.modal.open({
        parent: this,
        component: ModalSignDocument,
        props: {
          signCallback: async () =>
            await this.getCurrentDocumentDetails({
              process_file_id: this.id,
              process_id: this.processId
            })
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async viewSignedDocument() {
      this.isLoading = true;
      const result = await this.requestFile({
        fileIds: [{ process_file_id: this.id }],
        processId: this.processId,
        useContent: false
      });
      this.isLoading = false;
      if (result) {
        const [{ url_file }] = result;
        window.open(url_file, "_blank");
      }
    },

    isValidNUP(process) {
      if (process) {
        const nup = process.nup;
        return nup !== null && nup !== "" && Number(nup) > 0;
      }
      return false;
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    declineRequest() {
      const signatureRequest = this.currentDocument.SignatureFileHistory.filter(
        request =>
          request.status === STATUS.PENDING && request.user_id === this.user.id,
        null
      );

      if (signatureRequest)
        this.$buefy.modal.open({
          parent: this,
          component: modalDeclineRequest,
          props: {
            signatureList: signatureRequest,
            onClose: this.loadDocument
          },
          canCancel: ["escape", "x", "outside"],
          hasModalCard: true,
          trapFocus: true
        });
    },

    modalRefuseJustification(item) {
      this.$buefy.modal.open({
        parent: this,
        component: modalDeclineRequest,
        props: {
          onlyRead: true,
          fieldRefuseOption: item.refuse_option,
          fieldRefuseJustification: item.refuse_justification
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    modalViewGiveKnowledge(item) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalViewGiveKnowledge,
        props: {
          history: item
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    validateRequestSignature() {
      if (!this.currentDocument.process || !this.currentDocument.processFile)
        return false;
      const isUserResponsible = this.currentDocument.process.some(
        process => process.user_responsible_id === this.user.id
      );

      const documentNotProcessed =
        this.currentDocument.processFile.sequence === null &&
        this.currentDocument.processFile.user_id === this.user.id;

      return (
        Number(
          this.currentDocument.process[0].nup === 0 &&
            this.currentDocument.processFile.user_id === this.user.id
        ) ||
        isUserResponsible ||
        documentNotProcessed
      );
    },

    async openShareDetail(currentDocument) {
      this.isLoading = true;
      const process_id =
        this.processId ||
        currentDocument.process.find(process => process.id)?.id;
      const dispatch_id = currentDocument.processFile.dispatch?.id;
      const document_join_id = currentDocument.processFile.document_join;
      await this.requestListFileDraft({
        process_id,
        dispatch_id,
        document_join_id
      });
      this.isLoading = false;
      this.$buefy.modal.open({
        parent: this,
        component: ModalShareDetail,
        props: {
          processId: process_id,
          processFileRequestedId: currentDocument?.processFile?.id || ""
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalSchedulePublication() {
      if (this.hasDoeProcessFileId) {
        const document = {
          ...this.currentDocument.processFile,
          doe_process_file_id: this.$route.query.doeProcessFileId,
          programmed_date: this.isProgrammed
            ? this.$route.query.doeProgramDate
            : ""
        };
        document.document_type = document.document_type.type;
        document.nup = this.currentDocument.process[0].nup;
        this.$buefy.modal.open({
          parent: this,
          component: ModalSchedulePublication,
          props: {
            listDocuments: [document],
            changed: this.isProgrammed,
            onClose: () => this.reloadFile(),
            isHistory: true
          },
          canCancel: ["escape", "x", "outside"],
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.openAlertMessage(this.General.MESSAGES.WARNING_DOE_PROCESS_FILE);
      }
    },

    openModalDeclinePublication() {
      const document = { ...this.currentDocument.processFile };
      document.document_type = document.document_type.type;
      document.nup = this.currentDocument.process[0].nup;
      this.$buefy.modal.open({
        parent: this,
        component: ModalDeclinePublication,
        props: {
          listDocuments: [document],
          onClose: () => this.reloadFile()
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalDetailProgrammed(detail, changed) {
      const document = { ...this.currentDocument.processFile, ...detail };
      document.document_type = document.document_type.type;
      document.nup = this.currentDocument.process[0].nup;
      this.$buefy.modal.open({
        parent: this,
        component: ModalSchedulePublication,
        props: {
          listDocuments: [document],
          changed,
          onClose: () => this.reloadFile(),
          isHistory: true
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalDetailDeclinePublication(detail) {
      const document = { ...this.currentDocument.processFile, ...detail };
      document.document_type = document.document_type.type;
      document.nup = this.currentDocument.process[0].nup;
      this.$buefy.modal.open({
        parent: this,
        component: ModalDeclinePublication,
        props: {
          listDocuments: [document],
          onClose: () => this.reloadFile()
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalMandatorySignature(detail) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalDocMandatorySignature,
        props: {
          filled: detail.list
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalCanceled(detail) {
      const document = { ...this.currentDocument.processFile };
      document.document_type = document.document_type.type;
      document.nup = this.currentDocument.process[0].nup;
      this.$buefy.modal.open({
        parent: this,
        component: ModalSchedulePublication,
        props: {
          listDocuments: [document],
          changed: true,
          observation: detail.observation,
          cancelled: true,
          isHistory: true
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    showModalAuthorizeDOE() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${General.TEXTS.AUTHORIZE_PUBLICATION_DOE}?`,
          confirm: async () => {
            await this.authorizeToPublish({
              authorize: true,
              processFileId: this.currentDocument.id,
              processId: this.processId
            });
            await this.processFileRequest({ processId: this.processId });
            await this.processFileRequest({
              processId: this.processId,
              editable: true
            });
            this.reloadFile();
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalPublish(detail) {
      if (this.hasDoeProcessFileId || Boolean(detail)) {
        const document = {
          ...this.currentDocument.processFile,
          ...{
            ...detail,
            program_date:
              detail?.program_date || Number(this.$route.query.doeProgramDate)
          },
          doe_process_file_id: this.$route.query.doeProcessFileId
        };
        document.document_type = document.document_type.type;
        document.nup = this.currentDocument.process[0].nup;
        this.$buefy.modal.open({
          parent: this,
          component: ModalPublish,
          props: {
            listDocuments: [document],
            onClose: async () => {
              await this.processFileRequest({ processId: this.processId });
              await this.processFileRequest({
                processId: this.processId,
                editable: true
              });
              this.reloadFile();
            },
            isHistory: true
          },
          onCancel: () => (this.dueProgrammedList = []),
          canCancel: ["escape", "x", "outside"],
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.openAlertMessage(this.General.MESSAGES.WARNING_DOE_PROCESS_FILE);
      }
    },

    openModalChangePublish(detail) {
      const document = {
        process_file: { ...this.currentDocument.processFile },
        ...detail
      };
      document.process_file.document_type =
        document.process_file.document_type.type;
      document.process_file.nup = this.currentDocument.process[0].nup;
      this.$buefy.modal.open({
        parent: this,
        component: ModalChangePublishDOE,
        props: {
          listDocuments: [document],
          isHistory: true
        },
        canCancel: ["escape", "x", "outside"],
        hasModalCard: true,
        trapFocus: true
      });
    },

    openModalSendToPrintShop() {
      if (this.hasDoeProcessFileId) {
        const sentToPrintshopList = {
          ...this.currentDocument.processFile,
          process_file_id: this.currentDocument.processFile.id,
          sent_to_printshop: true,
          doe_process_file_id: this.$route.query.doeProcessFileId
        };
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}`,
            message: General.MESSAGES.DOC_SEND_TO_DOE,
            confirm: async () => {
              await this.setToSentToPrintshop([sentToPrintshopList]);
            },
            deny: () => null,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true,
          onCancel: () => null
        });
      } else {
        this.openAlertMessage(this.General.MESSAGES.WARNING_DOE_PROCESS_FILE);
      }
    },

    async loadDocument() {
      this.isLoading = true;
      const status = await this.getCurrentDocumentDetails({
        process_file_id: this.id,
        process_id: this.processId
      });

      if (!status) {
        const lastRouter = this.historyRouter.at(-2)?.path ?? "/app";
        if (lastRouter !== "/app") this.goBack();
        else
          this.processId
            ? this.$router.replace(
                `/app/processos/detalhamento/${this.processId}`
              )
            : this.$router.replace(`/app/`);
      }

      this.isLoading = false;
    },

    capacityToSign(historyList) {
      const signature = historyList.find(item => item.user_id === this.user.id);
      return signature ? signature.capacity : this.user.capacity.capacity_id;
    },

    getCapacityFormat(capacity_id) {
      const capacity = this.getCapacityById(capacity_id);
      if (!capacity) return "";
      return `${capacity.entity?.abbreviation}/${
        capacity.abbreviation?.includes("/")
          ? capacity.abbreviation.split("/")[1]
          : capacity.abbreviation
      }`;
    },

    setShowPDFTooltip(state) {
      this.showPDFTooltip = state;
    },

    async downloadOriginalFile() {
      this.isLoading = true;
      const result = await this.requestFile({
        fileIds: [{ process_file_id: this.id }],
        processId: this.processId,
        printer_version: true,
        useContent: false
      });
      this.isLoading = false;

      if (result) {
        const [{ url_file }] = result;
        downloadFileBlob({
          url: url_file,
          attachment_name: this.currentDocument.processFile.attachment_name
            .replace(/(\.\w+)/, "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\W/g, "_")
        });
      }
    },

    checkIsConfidential(callback = () => null) {
      if (this.isConfidential) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: this.General.MESSAGES.ALERT_DOCUMENT_SECRECY,
            invertOutline: true,
            confirm: callback,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        callback();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.section {
  background-color: $color-1;
  padding: 20px 0;

  .wrapper-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;

    @media screen and (max-width: $tablet) {
      flex-direction: column;
      gap: 10px;
    }
  }

  .wrapper-header div.header-item-right {
    padding-left: 20px;
    flex: 1;
  }

  .is-clickable {
    color: $color-7;
    &:hover {
      color: $primary;
    }
  }

  .custom-hr {
    border-bottom: 1px solid $custom-color-dcdcdc;
  }

  .custom-column-right {
    background-color: $color-1;
    -webkit-box-shadow: 0px 0px 5px 3px rgba(204, 202, 204, 0.5);
    -moz-box-shadow: 0px 0px 5px 3px rgba(204, 202, 204, 0.5);
    box-shadow: 0px 0px 5px 3px rgba(204, 202, 204, 0.5);
    border-radius: 6px;

    ul.custom-nav {
      display: flex;
      flex-direction: column;
      list-style: none;
      margin: 0 5px;
      gap: 7px;
    }
  }

  .wrapper-file-history {
    text-align: center;
    margin: 30px 0 0 0;

    .validated-doc-wrapper {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .validated-doc-wrapper::before {
      content: "";
      min-width: 10px;
      min-height: 10px;
      border-radius: 50%;
      margin-right: 5px;
      background-color: $primary;
    }
  }

  .wrapper-history-list {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    height: 450px;
    justify-content: flex-start;
    overflow: auto;
  }

  .wrapper-history-list div.pending,
  .wrapper-history-list div.signed,
  .wrapper-history-list div.refused,
  .wrapper-history-list div.validated,
  .wrapper-history-list div.no_effect,
  .wrapper-history-list div.give_knowledge,
  .wrapper-history-list div.to_publish_doe,
  .wrapper-history-list div.remove_to_publish_doe,
  .wrapper-history-list div.authorized_doe,
  .wrapper-history-list div.programmed_doe,
  .wrapper-history-list div.change_program_date_doe,
  .wrapper-history-list div.refused_doe,
  .wrapper-history-list div.published_doe,
  .wrapper-history-list div.change_publish_date_doe,
  .wrapper-history-list div.mandatory,
  .wrapper-history-list div.program_canceled_doe,
  .wrapper-history-list div.revert_no_effect {
    align-items: center;
    display: flex;
    gap: 5px;
    flex-direction: row;
    justify-content: center;
    padding: 5px 0;
  }

  .wrapper-history-list div.pending > div:nth-child(1) > div,
  .wrapper-history-list div.signed > div:nth-child(1) > div,
  .wrapper-history-list div.refused > div:nth-child(1) > div,
  .wrapper-history-list div.validated > div:nth-child(1) > div,
  .wrapper-history-list div.no_effect > div:nth-child(1) > div,
  .wrapper-history-list div.give_knowledge > div:nth-child(1) > div,
  .wrapper-history-list div.to_publish_doe > div:nth-child(1) > div,
  .wrapper-history-list div.remove_to_publish_doe > div:nth-child(1) > div,
  .wrapper-history-list div.authorized_doe > div:nth-child(1) > div,
  .wrapper-history-list div.programmed_doe > div:nth-child(1) > div,
  .wrapper-history-list div.change_program_date_doe > div:nth-child(1) > div,
  .wrapper-history-list div.refused_doe > div:nth-child(1) > div,
  .wrapper-history-list div.published_doe > div:nth-child(1) > div,
  .wrapper-history-list div.change_publish_date_doe > div:nth-child(1) > div,
  .wrapper-history-list div.mandatory > div:nth-child(1) > div,
  .wrapper-history-list div.program_canceled_doe > div:nth-child(1) > div,
  .wrapper-history-list div.revert_no_effect > div:nth-child(1) > div {
    border-radius: 50%;
    height: 10px;
    width: 10px;
  }

  .wrapper-history-list div.pending > div:nth-child(1) > div {
    background-color: $custom-color-fed328;
  }

  .wrapper-history-list div.signed > div:nth-child(1) > div {
    background-color: $custom-color-00b28f;
  }

  .wrapper-history-list div.refused > div:nth-child(1) > div {
    background-color: $custom-color-ff7675;
  }

  .wrapper-history-list div.validated > div:nth-child(1) > div {
    background-color: $custom-color-00b28f;
  }

  .wrapper-history-list div.no_effect > div:nth-child(1) > div {
    background-color: $custom-color-2f3133;
  }

  .wrapper-history-list div.give_knowledge > div:nth-child(1) > div {
    background-color: $custom-color-00b28f;
  }

  .wrapper-history-list div.to_publish_doe > div:nth-child(1) > div {
    background-color: $blue-oil;
  }

  .wrapper-history-list div.remove_to_publish_doe > div:nth-child(1) > div {
    background-color: $custom-color-ff7675;
  }

  .wrapper-history-list div.authorized_doe > div:nth-child(1) > div {
    background-color: $green-lawn;
  }

  .wrapper-history-list div.programmed_doe > div:nth-child(1) > div {
    background-color: $blue-oil;
  }

  .wrapper-history-list div.refused_doe > div:nth-child(1) > div {
    background-color: $custom-color-df5b5a;
  }

  .wrapper-history-list div.published_doe > div:nth-child(1) > div,
  .wrapper-history-list div.change_publish_date_doe > div:nth-child(1) > div {
    background-color: $green-dark;
  }

  .wrapper-history-list div.change_program_date_doe > div:nth-child(1) > div {
    background-color: $blue-dark;
  }

  .wrapper-history-list div.mandatory > div:nth-child(1) > div {
    background-color: $blue-dark;
  }

  .wrapper-history-list div.program_canceled_doe > div:nth-child(1) > div {
    background-color: $refused-color;
  }

  .wrapper-history-list div.revert_no_effect > div:nth-child(1) > div {
    background-color: $custom-color-2f3133;
  }

  .link {
    text-decoration: underline;
  }

  .doc-name {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 50vw;
    word-break: break-all;
  }

  .pdf-viewer-mobile {
    @media screen and (max-width: $tablet) {
      height: 45vh !important;
    }
  }
}
</style>

<style lang="scss">
.custom-nav {
  button {
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  button.is-medium {
    font-size: 1rem;
  }
}
</style>
